import DateService, { FMT, DayjsUnit } from '@/helpers/DateService';
export const MAX_WINDOW_MS = 366 * 86400000;
export const localDayRange = (firstDay, lastDay) => ({
    from: firstDay.startOf(DayjsUnit.Day).valueOf(),
    to: lastDay.endOf(DayjsUnit.Day).valueOf(),
});
export const normalizeIntervals = (list) => {
    if (list.length === 0)
        return [];
    const sorted = [...list].sort((a, b) => a.from - b.from);
    const merged = [sorted[0]];
    for (let index = 1; index < sorted.length; index++) {
        const current = sorted[index];
        const last = merged[merged.length - 1];
        if (current.from <= last.to + 1) {
            merged[merged.length - 1] = { from: last.from, to: Math.max(last.to, current.to) };
        }
        else {
            merged.push(current);
        }
    }
    return merged;
};
export const addInterval = (set, interval) => normalizeIntervals([...set, interval]);
export const removeInterval = (set, interval) => {
    const result = [];
    for (const current of set) {
        if (current.to < interval.from || current.from > interval.to) {
            result.push(current);
            continue;
        }
        if (current.from < interval.from) {
            result.push({ from: current.from, to: interval.from - 1 });
        }
        if (current.to > interval.to) {
            result.push({ from: interval.to + 1, to: current.to });
        }
    }
    return result;
};
export const subtractIntervals = (target, covered) => {
    let remaining = [target];
    for (const coveredInterval of normalizeIntervals(covered)) {
        remaining = remaining.flatMap((piece) => removeInterval([piece], coveredInterval));
    }
    return remaining;
};
export const isCovered = (target, set) => subtractIntervals(target, set).length === 0;
export const overlapsAny = (row, set) => {
    const salida = row.salida ?? Infinity;
    return set.some((interval) => row.entrada <= interval.to && salida >= interval.from);
};
export const rowSpan = (row) => ({
    entrada: DateService.toDayjs(row.FECHA_ENTRADA).valueOf(),
    salida: row.FECHA_SALIDA ? DateService.toDayjs(row.FECHA_SALIDA).valueOf() : null,
});
export const splitByMaxSpan = (interval, maxMs) => {
    const chunks = [];
    let start = interval.from;
    while (interval.to - start > maxMs) {
        const end = start + maxMs;
        chunks.push({ from: start, to: end });
        start = end + 1;
    }
    chunks.push({ from: start, to: interval.to });
    return chunks;
};
export const toQuery = (interval) => ({
    from: new Date(interval.from).toISOString(),
    to: new Date(interval.to).toISOString(),
});
const toValidMs = (value) => {
    if (!value)
        return null;
    const parsed = DateService.toDayjs(value);
    return parsed.isValid() ? parsed.valueOf() : null;
};
export const recursoSectorEstadoDates = (recursoSectores) => (recursoSectores ?? [])
    .flatMap((recursoSector) => recursoSector?.RECURSO_SECTOR_ESTADO ?? [])
    .map((estado) => estado?.FECHA)
    .filter((fecha) => Boolean(fecha));
export const emergencyGuardStart = (emergencia) => {
    const candidates = [];
    const creation = toValidMs(emergencia.FECHA_CREACION);
    if (creation !== null)
        candidates.push(creation);
    for (const sector of emergencia.SECTORES ?? []) {
        const rseDates = recursoSectorEstadoDates(sector?.RECURSOS);
        for (const fecha of rseDates) {
            const ms = toValidMs(fecha);
            if (ms !== null)
                candidates.push(ms);
        }
    }
    return candidates.length ? Math.min(...candidates) : null;
};
export const intervalsOverlap = (a, b) => a.from <= b.to && b.from <= a.to;
export const toValidDayjs = (value) => {
    const day = DateService.toDayjs(value);
    return day.isValid() ? day : null;
};
export const guardiasPreloadRange = (datosGuardias) => {
    const inits = datosGuardias.map((dato) => DateService.toDayjs(dato.init, FMT.ISODate));
    const ends = datosGuardias.map((dato) => DateService.toDayjs(dato.end, FMT.ISODate));
    const minInit = inits.reduce((min, day) => (day.isBefore(min) ? day : min));
    const maxEnd = ends.reduce((max, day) => (day.isAfter(max) ? day : max));
    return localDayRange(minInit, maxEnd.subtract(1, DayjsUnit.Day));
};
const FALLBACK_MARGIN_MS = 86400000;
export const isBackendFallback = (rows, interval) => rows.some((row) => {
    const { entrada, salida } = rowSpan(row);
    const salidaMs = salida ?? Infinity;
    return salidaMs < interval.from - FALLBACK_MARGIN_MS || entrada > interval.to + FALLBACK_MARGIN_MS;
});
